import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { api } from '../services/api';

// Admin v2 · V3/V5 — the DO-NOW queues. Live state for founder action;
// deliberately NOT affected by any period selector. Mounted on the Dashboard
// now (V5 interim) and on the Overview page when it lands (V8).

// wa.me wants digits only, country code included, no '+'.
const waLink = (phone) => `https://wa.me/${String(phone || '').replace(/\D/g, '')}`;

function WhatsAppButton({ phone }) {
  if (!phone) return null;
  return (
    <a
      href={waLink(phone)}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1.5 border border-teacher-green text-teacher-green rounded-lg px-2.5 py-1.5 text-xs font-semibold hover:bg-teacher-green-light transition-colors shrink-0"
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
      </svg>
      WhatsApp
    </a>
  );
}

function QueueCard({ title, badge, badgeClass, children, footer }) {
  return (
    <div className="bg-white border border-teacher-border rounded-xl p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-teacher-text-primary">{title}</h3>
        <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 ${badgeClass}`}>{badge}</span>
      </div>
      <div className="flex flex-col gap-2.5 flex-1">{children}</div>
      {footer && <div className="pt-2.5 border-t border-teacher-bg-secondary text-xs text-teacher-text-secondary">{footer}</div>}
    </div>
  );
}

export default function QueueCards({ data = null }) {
  const [queues, setQueues] = useState(data);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (data) { setQueues(data); return; }
    api.getQueues()
      .then((r) => setQueues(r.data))
      .catch((e) => setError(e.message));
  }, [data]);

  if (error) {
    return <div className="text-sm text-teacher-red">Queues failed to load: {error}</div>;
  }
  if (!queues) {
    return <div className="text-sm text-teacher-text-tertiary">Loading queues…</div>;
  }

  const { trials_expiring, never_answered, errors } = queues;
  const wave = trials_expiring.wave;

  return (
    <div>
      <div className="flex items-baseline gap-2.5 mb-3">
        <h2 className="text-xs font-bold tracking-wider text-teacher-text-secondary">DO NOW</h2>
        <span className="text-xs text-teacher-text-tertiary">live state — not affected by any period</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3.5">

        <QueueCard
          title="Trials ending soon"
          badge={trials_expiring.count}
          badgeClass="bg-teacher-purple-light text-teacher-purple"
          footer={wave
            ? <>{wave.count} trials end <span className="font-semibold">{format(new Date(`${wave.startDate}T00:00:00+05:30`), 'MMM d')}–{format(new Date(`${wave.endDate}T00:00:00+05:30`), 'MMM d')}</span> — plan the conversion push now.</>
            : 'No expiry wave in the next 60 days.'}
        >
          {trials_expiring.rows.length === 0 && (
            <div className="text-xs text-teacher-text-tertiary">Nothing ending in the next 7 days.</div>
          )}
          {trials_expiring.rows.slice(0, 4).map((r) => (
            <div key={r.uid} className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <Link to={`/users/${r.uid}`} className="text-sm font-semibold text-teacher-text-primary hover:text-primary-700 truncate block">
                  {r.name} <span className="font-normal text-teacher-text-tertiary">· {r.answers} answers</span>
                </Link>
                <div className="text-xs font-medium text-teacher-amber">
                  ends {format(new Date(r.endsAt), 'EEE d MMM')} · {r.daysLeft}d
                </div>
              </div>
              <WhatsAppButton phone={r.phone} />
            </div>
          ))}
        </QueueCard>

        <QueueCard
          title="Signed up, never answered"
          badge={never_answered.count}
          badgeClass="bg-teacher-red-light text-teacher-red"
          footer={<Link to="/users?activity=never_answered" className="font-semibold text-primary-700 hover:text-primary-800">open all {never_answered.count} in Students →</Link>}
        >
          {never_answered.rows.slice(0, 3).map((r) => (
            <div key={r.uid} className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <Link to={`/users/${r.uid}`} className="text-sm font-semibold text-teacher-text-primary hover:text-primary-700 truncate block">
                  {r.name} {r.state && <span className="font-normal text-teacher-text-tertiary">· {r.state}</span>}
                </Link>
                <div className="text-xs text-teacher-text-tertiary">
                  joined {r.signedUpAt ? format(new Date(r.signedUpAt), 'MMM d') : '—'}
                  {r.lastActive ? ` · opened ${formatDistanceToNow(new Date(r.lastActive), { addSuffix: true })}` : ' · never opened since'}
                </div>
              </div>
              <WhatsAppButton phone={r.phone} />
            </div>
          ))}
        </QueueCard>

        <QueueCard
          title="Errors students hit · 24h"
          badge={errors.generateFailed24h === null ? '—' : errors.generateFailed24h}
          badgeClass={errors.generateFailed24h ? 'bg-teacher-amber-light text-teacher-amber' : 'bg-teacher-bg-secondary text-teacher-text-secondary'}
          footer={
            <div className="flex gap-4">
              <a href="https://jeevibe.sentry.io" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary-700 hover:text-primary-800">Sentry →</a>
              <a href="https://console.firebase.google.com/project/jeevibe/crashlytics" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary-700 hover:text-primary-800">Crashlytics →</a>
            </div>
          }
        >
          <div className="text-xs text-teacher-text-secondary leading-relaxed">
            {errors.generateFailed24h === null
              ? 'Session-event pipeline unreadable right now — count withheld rather than shown as a green light.'
              : errors.generateFailed24h === 0
                ? 'No failed session generations in the last 24h (build 153+ clients report these). Crash-level errors live in Sentry / Crashlytics below.'
                : `${errors.generateFailed24h} failed session generation${errors.generateFailed24h === 1 ? '' : 's'} in the last 24h — check the Entry Funnel for which surface.`}
          </div>
        </QueueCard>
      </div>
    </div>
  );
}
